import './styles.css';
import {
  renderLiteral,
  renderContext,
  renderList,
  renderInvocation,
  renderRelation,
  renderFunctionDefinition,
} from './views';
import { renderMlDecision } from './MlDecisionEditor';
import { EXPRESSION_TYPES, LOGIC_TYPES, createExpression, getExpressionTypeLabel } from './shared';
import { ScopePanel } from '../scope/ScopePanel';
import { computeScope } from '../scope/computeScope';
import { resolveDecisionInputs } from '../scope/decisionInputs';
import { sanitizeFeelIdent } from '../feel/ident';

// Dispatcher class for boxed expressions. Plays the same role the
// DecisionTableEditor plays for decision tables: mounts on a DOM container,
// takes a moddle Decision / BKM, renders the appropriate boxed expression,
// and rebuilds on every mutation.
//
// The `render` signature accepts either `{ decision, expression }`,
// `{ bkm, expression }`, or a raw expression node. Nested expressions are
// rendered by recursing through `_dispatch()` so any child (a Context value,
// a List element, an Invocation binding, a Function body, …) can itself be
// any boxed type.

export class BoxedExpressionEditor {
  constructor(container, { moddle, onChange, onSwitchLogicType, onPromoteOrphan, onOpened } = {}) {
    if (!container) throw new Error('[BoxedExpressionEditor] container required');
    if (!moddle) throw new Error('[BoxedExpressionEditor] moddle required');
    this._container = container;
    this._moddle = moddle;
    this._onChange = onChange || (() => {});
    // Optional handler used to cross the boxed-expression / DecisionTable
    // boundary. When provided, the root-type menu lists all logic types
    // (including DecisionTable) and delegates the switch to the host.
    this._onSwitchLogicType = typeof onSwitchLogicType === 'function' ? onSwitchLogicType : null;
    // Optional handler invoked when the user clicks the ＋ on a
    // "Variables no traqueadas" chip in the ScopePanel. Receives
    // (name, target) where target is the current Decision or BKM.
    this._onPromoteOrphan = typeof onPromoteOrphan === 'function' ? onPromoteOrphan : null;
    // PR 1 §4.1 — emitted whenever the editor (re)mounts a root expression so
    // upstream listeners (Atlas chat) know which boxed kind is active.
    this._onOpened = typeof onOpened === 'function' ? onOpened : null;
    this._lastOpenedKind = null;
    this._decision = null;
    this._bkm = null;
    this._expression = null;
    this._definitions = null;
    this._container.classList.add('epx-dmn-expr');

    // Two-column layout: main editor + scope side panel. The main column is
    // torn down on every redraw, the scope panel persists across redraws.
    this._wrap = document.createElement('div');
    this._wrap.className = 'epx-dmn-expr__wrap';
    this._mainEl = document.createElement('div');
    this._mainEl.className = 'epx-dmn-expr__main';
    this._scopeEl = document.createElement('div');
    this._wrap.appendChild(this._mainEl);
    this._wrap.appendChild(this._scopeEl);
    this._container.appendChild(this._wrap);
    this._scopePanel = new ScopePanel(this._scopeEl, {
      onSetTypeRef: (sourceId, typeRef) => this._setInputDataTypeRef(sourceId, typeRef),
      onPromoteOrphan: this._onPromoteOrphan
        ? (name) => this._onPromoteOrphan(name, this._bkm || this._decision)
        : null,
    });
  }

  _setInputDataTypeRef(sourceId, typeRef) {
    if (!sourceId || !this._definitions) return;
    const drg = (this._definitions.drgElement || []).find((el) => el.id === sourceId);
    if (!drg || drg.$type !== 'dmn:InputData') return;
    if (!drg.variable) {
      drg.variable = this._moddle.create('dmn:InformationItem', {
        id: (drg.id || 'Var') + '_var',
        name: sanitizeFeelIdent(drg.name) || drg.id,
      });
    }
    drg.variable.typeRef = typeRef || null;
    this._refreshScope();
    this._onChange({ kind: 'inputData.typeRef', element: drg, typeRef });
  }

  setDefinitions(definitions) {
    this._definitions = definitions || null;
    this._refreshScope();
  }

  _refreshScope() {
    if (!this._scopePanel) return;
    const target = this._bkm || this._decision;
    const scope = target && this._definitions
      ? computeScope(this._definitions, target)
      : [];
    this._scopePanel.setScope(scope);
    const orphans = target && this._definitions
      ? (resolveDecisionInputs(this._definitions, target).orphans || [])
      : [];
    this._scopePanel.setOrphans(orphans);
  }

  render(arg) {
    if (!arg) {
      this._decision = this._bkm = this._expression = null;
    } else if (arg.$type && (arg.$type.startsWith('dmn:') || arg.$type.startsWith('epx:'))) {
      // Raw expression
      this._decision = this._bkm = null;
      this._expression = arg;
    } else {
      this._decision = arg.decision || null;
      this._bkm = arg.bkm || null;
      if (arg.expression) this._expression = arg.expression;
      else if (arg.decision && arg.decision.decisionLogic) this._expression = arg.decision.decisionLogic;
      else if (arg.bkm && arg.bkm.encapsulatedLogic)       this._expression = arg.bkm.encapsulatedLogic;
      else this._expression = null;
    }
    this._redraw();
    this._notifyOpened();
  }

  /**
   * PR 1 §4.1 — surface the active expression kind to the host (e.g. Atlas
   * chat) so context-aware chips can react. Emits at most once per kind
   * transition to keep the event idempotent.
   */
  _notifyOpened() {
    if (!this._onOpened) return;
    const kind = this._expressionKind(this._expression);
    if (kind === this._lastOpenedKind) return;
    this._lastOpenedKind = kind;
    try {
      this._onOpened({ kind, expr: this._expression || null });
    } catch (err) {
      console.error('[BoxedExpressionEditor] onOpened handler error:', err);
    }
  }

  _expressionKind(expr) {
    if (!expr || !expr.$type) return null;
    switch (expr.$type) {
      case 'dmn:LiteralExpression':       return 'literal';
      case 'dmn:Context':                 return 'context';
      case 'dmn:List':                    return 'list';
      case 'dmn:Invocation':              return 'invocation';
      case 'dmn:Relation':                return 'relation';
      case 'dmn:FunctionDefinition':      return 'function';
      case 'dmn:DecisionTable':           return 'decisionTable';
      case 'epx:MachineLearningDecision': return 'ml';
      default:                            return null;
    }
  }

  clear() {
    this._decision = null;
    this._bkm = null;
    this._expression = null;
    if (this._mainEl) this._mainEl.innerHTML = '';
    this._refreshScope();
  }

  destroy() {
    if (this._scopePanel) {
      try { this._scopePanel.destroy(); } catch { /* ignore */ }
      this._scopePanel = null;
    }
    this._decision = null;
    this._bkm = null;
    this._expression = null;
    this._container.innerHTML = '';
    this._container.classList.remove('epx-dmn-expr');
  }

  refresh() {
    this._redraw();
  }

  getExpression() {
    return this._expression;
  }

  getDecision() {
    return this._decision;
  }

  getBkm() {
    return this._bkm;
  }

  // Replace the active expression with a fresh one of a given boxed type.
  // Writes back into the owning decision so the round-trip holds.
  //
  // For BKMs this is intentionally a no-op: a BKM's `encapsulatedLogic`
  // must always be a FunctionDefinition per the DMN spec. The body of that
  // FunctionDefinition is switched through the per-body type picker in
  // renderFunctionDefinition, not through this root-level chip.
  setExpressionType(newType) {
    if (!newType) return;
    if (this._bkm) return;
    const fresh = createExpression(this._moddle, newType);
    if (this._decision) {
      this._decision.decisionLogic = fresh;
    }
    this._expression = fresh;
    this._fireChange();
    this._redraw();
    this._notifyOpened();
  }

  _fireChange() {
    try {
      this._onChange({
        expression: this._expression,
        decision: this._decision,
        bkm: this._bkm,
      });
    } catch (err) {
      console.error('[BoxedExpressionEditor] onChange error', err);
    }
  }

  _redraw() {
    if (!this._mainEl) return;
    this._mainEl.innerHTML = '';
    if (!this._expression) {
      const empty = document.createElement('div');
      empty.className = 'epx-dmn-expr__empty';
      empty.textContent = 'No decision logic. Pick a Decision or BKM with a boxed expression.';
      this._mainEl.appendChild(empty);
      this._refreshScope();
      return;
    }

    const header = document.createElement('div');
    header.className = 'epx-dmn-expr__header';
    const title = document.createElement('span');
    title.className = 'epx-dmn-expr__title';
    title.textContent =
      (this._decision && this._decision.name) ||
      (this._bkm && this._bkm.name) ||
      'Expression';
    header.appendChild(title);

    const typeLabel = document.createElement('span');
    typeLabel.className = 'epx-dmn-expr__type-chip';
    typeLabel.textContent = getExpressionTypeLabel(this._expression.$type);
    if (this._bkm) {
      // BKM root is fixed to FunctionDefinition — the body switcher lives
      // inside renderFunctionDefinition.
      typeLabel.classList.add('epx-dmn-expr__type-chip--readonly');
      typeLabel.title = 'A BKM is always a Function Definition. Switch the body type from inside.';
    } else {
      typeLabel.title = 'Change the root expression type';
      typeLabel.addEventListener('click', (e) => this._openRootTypeMenu(e.currentTarget));
    }
    header.appendChild(typeLabel);

    this._mainEl.appendChild(header);

    const body = document.createElement('div');
    body.className = 'epx-dmn-expr__body';

    const ctx = {
      moddle: this._moddle,
      onChange: () => { this._fireChange(); this._redraw(); },
      render: (expr, c) => this._dispatch(expr, c),
      getScope: () => {
        const target = this._bkm || this._decision;
        if (!target || !this._definitions) return [];
        return computeScope(this._definitions, target);
      },
    };

    body.appendChild(this._dispatch(this._expression, ctx));
    this._mainEl.appendChild(body);
    this._refreshScope();
  }

  _dispatch(expr, ctx) {
    if (!expr) {
      const ph = document.createElement('div');
      ph.className = 'epx-dmn-expr__placeholder';
      ph.textContent = '—';
      return ph;
    }
    switch (expr.$type) {
      case 'dmn:LiteralExpression':  return renderLiteral(expr, ctx);
      case 'dmn:Context':            return renderContext(expr, ctx);
      case 'dmn:List':               return renderList(expr, ctx);
      case 'dmn:Invocation':         return renderInvocation(expr, ctx);
      case 'dmn:Relation':           return renderRelation(expr, ctx);
      case 'dmn:FunctionDefinition': return renderFunctionDefinition(expr, ctx);
      case 'epx:MachineLearningDecision': return renderMlDecision(expr, ctx, this._decision);
      case 'dmn:DecisionTable': {
        const p = document.createElement('div');
        p.className = 'epx-dmn-expr__redirect';
        p.textContent = 'This decision is a Decision Table. Switch to the Decision Table view to edit it.';
        return p;
      }
      default: {
        const p = document.createElement('div');
        p.className = 'epx-dmn-expr__placeholder';
        p.textContent = `Unsupported expression type: ${expr.$type}`;
        return p;
      }
    }
  }

  _openRootTypeMenu(anchor) {
    document.querySelectorAll('.epx-dmn-expr__type-menu').forEach((m) => m.remove());
    const menu = document.createElement('div');
    menu.className = 'epx-dmn-expr__type-menu';
    const rect = anchor.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 2) + 'px';
    // When the host supplied an onSwitchLogicType callback we list the full
    // logic-type set (including DecisionTable) and delegate the switch.
    // Otherwise fall back to boxed-expression-only options handled in-place.
    const options = this._onSwitchLogicType ? LOGIC_TYPES : EXPRESSION_TYPES;
    options.forEach((opt) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'epx-dmn-expr__type-menu-item';
      if (this._expression && opt.type === this._expression.$type) b.classList.add('is-active');
      b.textContent = opt.label;
      b.addEventListener('click', () => {
        menu.remove();
        if (this._onSwitchLogicType) {
          if (this._expression && opt.type === this._expression.$type) return;
          this._onSwitchLogicType(opt.type);
        } else {
          this.setExpressionType(opt.type);
        }
      });
      menu.appendChild(b);
    });
    document.body.appendChild(menu);
    const close = (e) => {
      if (!menu.contains(e.target) && e.target !== anchor) {
        menu.remove();
        document.removeEventListener('mousedown', close);
      }
    };
    setTimeout(() => document.addEventListener('mousedown', close), 0);
  }
}
